import React, { useState } from "react";
import axios from "axios";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { toast } from "react-toastify";
import { TbFileDownload } from "react-icons/tb";
import htmlToExcel from "html-table-to-excel";

const MONTHS = [
  { value: "1", label: "January" },
  { value: "2", label: "February" },
  { value: "3", label: "March" },
  { value: "4", label: "April" },
  { value: "5", label: "May" },
  { value: "6", label: "June" },
  { value: "7", label: "July" },
  { value: "8", label: "August" },
  { value: "9", label: "September" },
  { value: "10", label: "October" },
  { value: "11", label: "November" },
  { value: "12", label: "December" },
];

const thCls =
  "px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide whitespace-nowrap border-r last:border-r-0";
const tdCls =
  "px-4 py-2.5 text-xs text-gray-700 border-r last:border-r-0 whitespace-nowrap";

function TransactionExport() {
  const [selectedMonth, setSelectedMonth] = useState("");
  const [yearSelected, setYearSelected] = useState(new Date());
  const [records, setRecords] = useState({});
  const [isLoading, setIsLoading] = useState(false);

  const formatRecord = (data) => {
    if (!data || data.length === 0) return {};
    return data.reduce((acc, item) => {
      const g = item.group_name;
      if (!acc[g]) acc[g] = [];
      acc[g].push(item);
      return acc;
    }, {});
  };

  const getMonthlyRecord = async () => {
    setIsLoading(true);
    try {
      const res = await axios.get(
        `https://eps.elkomprintama.com/get/api/store/transaction/report?month=${selectedMonth}&year=${yearSelected.getFullYear()}`,
      );
      setRecords(formatRecord(res.data.result));
    } catch {
      toast.error("Failed to fetch monthly report.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!selectedMonth) {
      toast.warning("Please select a month.");
      return;
    }
    getMonthlyRecord();
  };

  const exportToExcel = () => {
    const table = document.getElementById("monthlyTable");
    if (!table) return;
    htmlToExcel(
      table,
      `monthly_report_${selectedMonth}_${yearSelected.getFullYear()}.xlsx`,
    );
  };

  const groupEntries = Object.entries(records);
  const hasData = groupEntries.length > 0;
  const monthLabel = MONTHS.find((m) => m.value === selectedMonth)?.label || "";

  return (
    <div className="flex flex-col" style={{ maxHeight: "calc(100vh - 80px)" }}>
      {/* Header */}
      <div className="w-full rounded-lg bg-teal-700 mb-4 flex-shrink-0">
        <label className="block uppercase text-white tracking-wide text-5xl text-center font-bold py-2">
          Monthly Report
        </label>
      </div>

      {/* Body card */}
      <div className="flex-1 min-h-0 flex flex-col bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden">
        {/* Controls */}
        <form
          onSubmit={handleSubmit}
          className="flex-shrink-0 px-6 py-5 border-b border-gray-100 space-y-4"
        >
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            {/* Year picker */}
            <div>
              <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">
                Year
              </label>
              <DatePicker
                wrapperClassName="w-full"
                className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-1 focus:ring-teal-400"
                selected={yearSelected}
                onChange={setYearSelected}
                showYearPicker
                dateFormat="yyyy"
              />
            </div>
            {/* Month selector */}
            <div className="md:col-span-3">
              <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">
                Month
              </label>
              <select
                className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-1 focus:ring-teal-400 bg-white"
                value={selectedMonth}
                onChange={(e) => setSelectedMonth(e.target.value)}
              >
                <option value="">Select Month</option>
                {MONTHS.map((m) => (
                  <option key={m.value} value={m.value}>
                    {m.label}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <button
            type="submit"
            disabled={isLoading}
            className="w-full py-2.5 rounded-xl text-sm font-bold text-white bg-teal-700 hover:bg-teal-800 disabled:opacity-60 transition"
          >
            {isLoading ? "Loading…" : "Check Records"}
          </button>
        </form>

        {/* Table */}
        <div className="flex-1 min-h-0 overflow-auto">
          {!hasData ? (
            <div className="h-full flex items-center justify-center text-gray-400 italic text-sm">
              {isLoading
                ? "Loading…"
                : "Select a month and click Check Records"}
            </div>
          ) : (
            <table id="monthlyTable" className="w-full text-xs border-collapse">
              <thead className="sticky top-0 bg-gray-700 text-white uppercase">
                <tr>
                  <th className={thCls}>No</th>
                  <th className={thCls}>Product Code</th>
                  <th className={thCls}>Product Name</th>
                  <th className={thCls}>Stock QTY</th>
                </tr>
              </thead>
              <tbody>
                {groupEntries.map(([groupName, groupItems], gi) => (
                  <React.Fragment key={`g-${gi}`}>
                    <tr className="bg-teal-700 border-t-4 border-teal-400">
                      <td
                        colSpan={4}
                        className="px-4 py-2 text-xs font-bold text-white uppercase tracking-widest"
                      >
                        {groupName} · {groupItems.length} item
                        {groupItems.length !== 1 ? "s" : ""}
                      </td>
                    </tr>
                    {groupItems.map((data, i) => (
                      <tr
                        key={`${gi}-${i}`}
                        className={`border-b hover:bg-teal-50 ${i % 2 === 0 ? "bg-gray-50" : "bg-white"}`}
                      >
                        <td className={`${tdCls} text-gray-400`}>{i + 1}</td>
                        <td className={tdCls}>{data.product_code}</td>
                        <td className={`${tdCls} font-medium text-gray-800`}>
                          {data.product_name}
                        </td>
                        <td className={`${tdCls} font-bold text-teal-700`}>
                          {data.qty}
                        </td>
                      </tr>
                    ))}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* Footer with export */}
        {hasData && (
          <div className="flex-shrink-0 px-5 py-3 border-t border-gray-100 bg-gray-50 flex items-center justify-between">
            <span className="text-xs text-gray-400">
              {monthLabel} {yearSelected.getFullYear()} —&nbsp;
              {groupEntries.reduce((s, [, items]) => s + items.length, 0)} items
            </span>
            <button
              onClick={exportToExcel}
              className="flex items-center gap-2 px-4 py-2 rounded-lg text-xs font-bold text-white bg-teal-700 hover:bg-teal-800 transition"
            >
              <TbFileDownload size={16} />
              Download Excel
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

export default TransactionExport;
